import { useCallback, useState, type Dispatch, type SetStateAction } from "react";

/** Reset context-owned UI state without remounting the surrounding presentation. */
export function useContextState<T>(contextKey: string, createValue: () => T): [T, Dispatch<SetStateAction<T>>] {
  const [stored, setStored] = useState(() => ({ scope: { contextKey }, value: createValue() }));
  let current = stored;
  if (stored.scope.contextKey !== contextKey) {
    current = { scope: { contextKey }, value: createValue() };
    // Adjust before children commit, so dialogs never render under another wallet.
    setStored(current);
  }
  const scope = current.scope;
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setStored((previous) => {
      // Object identity also rejects late completions after an A -> B -> A trip.
      if (previous.scope !== scope) return previous;
      const value = typeof next === "function" ? (next as (value: T) => T)(previous.value) : next;
      return Object.is(value, previous.value) ? previous : { scope, value };
    });
  }, [scope]);
  return [current.value, setValue];
}
